import React, { useState } from "react";
import DetailPatientPackageComponents from "../components";
import { useQuery } from "@tanstack/react-query";
import { formatNumberWithCurrency, getParams } from "../../../../utils/utils";
import {
  getDetailPatientPackages,
  getPatientPackagesHistory,
} from "../../../../services/Handlers/PackageService";
import LoadingSection from "../../../components/LoadingSection";
import { typeDiscountObj } from "../../../../utils/constants";
import { getPatientSingle } from "../../../../services/PatientService";
import { parsePatientDob } from "../../../../utils/patientDob";
import { useNotification } from "../../../../context/NotificationProvider";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import { useAuth } from "../../../../context/AuthProvider";

export default function DetailPatientPackage() {
  const id = getParams("id");
  const { userDetail } = useAuth();
  const notification = useNotification();
  const localize = useLocalization();
  const [activePackage, setDataActivePackage] = useState([]);
  const [patientId, setPatientId] = useState(null);
  const [dataPatient, setDataPatient] = useState(null);

  const [packageHistory, setPackageHistory] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);

  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(5);
  const [sortState, setSortState] = useState({ key: "", value: "" });

  const { isLoading, isFetching } = useQuery(
    ["DetailPatientPackage"],
    () => getDetailPatientPackages(id),
    {
      enabled: !!id,
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          const response = res.data;
          if (response) {
            const dataActivePackage = [];

            let subTotal = response?.package_item?.amount || 0;
            if (
              response?.package_item?.type_discount ===
                typeDiscountObj.NUMBER &&
              response?.package_item?.discount > 0
            ) {
              subTotal -= +response.package_item.discount;
            } else if (
              response?.package_item?.type_discount ===
                typeDiscountObj.PERCENTAGE &&
              response?.package_item?.discount > 0
            ) {
              subTotal -=
                (parseFloat(response?.package_item?.discount) / 100) * subTotal;
            }

            dataActivePackage.push({
              package_name: response?.package_name,
              remaining_visits: response?.remaining_visits,
              last_reservation_date: response?.last_reservation_date,
              price: formatNumberWithCurrency(
                response?.package_item?.amount || 0
              ),
              dicount:
                response?.package_item.type_discount === typeDiscountObj.NUMBER
                  ? formatNumberWithCurrency(response?.package_item.discount)
                  : response?.package_item?.discount
                  ? `${response?.package_item?.discount}%`
                  : "-",
              sub_total: formatNumberWithCurrency(subTotal),
            });

            setDataActivePackage(dataActivePackage);
            setPatientId(response?.patient_id);
          }
        }
      },
    }
  );

  const { isLoading: isLoadingPatient } = useQuery(
    ["DetailPatient", patientId],
    () => getPatientSingle(patientId),
    {
      enabled: !!patientId,
      onSuccess: (res) => {
        const response = res.data;
        if (response) {
          setDataPatient({
            name: response?.name,
            phoneNumber: response?.phoneNumber,
            dateOfBirth: parsePatientDob(response?.dob).isValid()
              ? parsePatientDob(response?.dob).format("DD MMM YYYY")
              : "",
            gender: response?.gender,
            address: response?.address,
            remark: response?.remark,
            allergies: response?.allergy || [],
            identificationType: response?.identificationType || "KTP",
            identificationNumber: response?.identificationNumber,
            medicalRecordNumber: response?.medicalRecordNumber,
          });
        }
      },
    }
  );

  const { isFetching: isLoadingHistory } = useQuery(
    [
      "GetHistoryPackagePatient",
      userDetail?.businessId,
      page,
      sizePerPage,
      sortState,
      patientId,
    ],
    () =>
      getPatientPackagesHistory({
        businessId: userDetail?.businessId,
        page,
        size: sizePerPage,
        sort_by: sortState?.key,
        sort_order: sortState?.value,
        patient_id: patientId,
      }),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          const response = res.data;

          if (response?.data.length > 0) {
            const processedHistory = (response?.data || []).map((item) => {
              let historySubTotal = item.amount || 0;

              if (
                item.type_discount === typeDiscountObj.NUMBER &&
                item.discount > 0
              ) {
                historySubTotal -= item.discount;
              } else if (
                item.type_discount === typeDiscountObj.PERCENTAGE &&
                item.discount > 0
              ) {
                historySubTotal -=
                  (parseFloat(item.discount) / 100) * historySubTotal;
              }

              return {
                ...item,
                sub_total: historySubTotal,
                formatted_sub_total: formatNumberWithCurrency(historySubTotal),
                formatted_amount: formatNumberWithCurrency(item.amount || 0),
                formatted_discount:
                  item.type_discount === typeDiscountObj.NUMBER
                    ? formatNumberWithCurrency(item.discount)
                    : item.discount
                    ? `${item.discount}%`
                    : "-",
              };
            });

            setPackageHistory(processedHistory || []);
          } else {
            setPackageHistory([]);
          }
          setTotalEntries(res?.data?.total);
          setTotalPage(Math.ceil(res?.data?.total / sizePerPage));
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      enabled: !!userDetail?.businessId && !!patientId,
    }
  );

  const handlePagination = (value) => {
    setPage(value);
  };

  const handleSetSizePerPage = (size) => {
    setPage(0);
    setSizePerPage(size);
  };

  if (isLoading || isFetching || isLoadingPatient) {
    return <LoadingSection />;
  }

  return (
    <DetailPatientPackageComponents
      totalPage={totalPage}
      totalEntries={totalEntries}
      dataPatient={dataPatient}
      activePackage={activePackage}
      packageHistory={packageHistory}
      activePage={page}
      sortState={sortState}
      sizePerPage={sizePerPage}
      handlePagination={handlePagination}
      handleSetSizePerPage={handleSetSizePerPage}
      setSortState={setSortState}
      isLoadingHistory={isLoadingHistory}
    />
  );
}
